import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { backendGet } from "../../services/dataService.js";
import { Card, SectionHeader } from "../../theme/components/index.js";
import MailDetailPanel from "./MailDetailPanel.jsx";
import MailClaimAssignment from "./MailClaimAssignment.jsx";

/**
 * MailDetailSplit — the click-dummy's inline master-detail panel that sits below
 * the inbox list: a bordered card split into the mail content (left) and the
 * claim-assignment sidebar (right). Replaces the old MailDetailModal so the
 * Posteingang matches the dummy (persistent split-pane, not a modal). Shows the
 * dummy's empty states until a mail is selected.
 */
export default function MailDetailSplit({ mailId }) {
  const { enqueueSnackbar } = useSnackbar();
  const [mail, setMail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!mailId) {
      setMail(null);
      return;
    }
    backendGet(`data/incoming-mails/${mailId}`, {
      label: "E-Mail laden",
      enqueueSnackbar,
      options: { params: { include: "attachments,schaden" } },
      setIsLoading,
      setResult: (res) => setMail(res?.data ?? res),
    });
  }, [mailId, enqueueSnackbar]);

  return (
    <Card variant="surface" sx={{ mt: 3, p: 0, overflow: "hidden" }}>
      <Box sx={{ display: "flex", minHeight: 220 }}>
        {/* Left — mail content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                minHeight: 220,
              }}
            >
              <CircularProgress size={20} />
              <Typography>Wird geladen…</Typography>
            </Box>
          ) : mail ? (
            <MailDetailPanel mail={mail} isIncoming />
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 220,
                p: 3,
                textAlign: "center",
              }}
            >
              <Typography sx={{ color: "#6b7280", fontSize: "14px" }}>
                Wählen Sie eine E-Mail aus, um die Details anzuzeigen.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Right — claim assignment */}
        <Box
          sx={{
            width: 320,
            flexShrink: 0,
            borderLeft: "1px solid",
            borderColor: "#e7edf4",
            p: "20px",
          }}
        >
          <SectionHeader sx={{ mb: 1.5 }}>Zuordnung</SectionHeader>
          {mail ? (
            <MailClaimAssignment mail={mail} />
          ) : (
            <Typography sx={{ color: "#9ca3af", fontSize: "13px" }}>
              Keine E-Mail ausgewählt.
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  );
}

MailDetailSplit.propTypes = {
  mailId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
